import { useState, useEffect, useCallback } from 'react';
import { useImpersonation } from '@center-technologies/shared-api-clients';
import { useApiClientsOrNull } from '../context/ApiClientsContext';
import { AuditEntry } from 'types/channel';
import { getItemAuditTrail } from '../api/marketplaceApi';

const DEFAULT_PAGE_SIZE = 20;

export interface UseItemAuditResult {
  entries: AuditEntry[];
  total: number;
  hasMore: boolean;
  loading: boolean;
  error: string | null;
  loadMore: () => void;
  refetch: () => void;
}

export function useItemAudit(
  channelId: string,
  itemId: string | null,
  pageSize: number = DEFAULT_PAGE_SIZE
): UseItemAuditResult {
  const apiClients = useApiClientsOrNull();
  const { impersonationDetails } = useImpersonation();
  const organizationId = impersonationDetails?.organizationId;

  const [entries, setEntries] = useState<AuditEntry[]>([]);
  const [total, setTotal] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [offset, setOffset] = useState(0);
  const [fetchTrigger, setFetchTrigger] = useState(0);

  const refetch = useCallback(() => {
    setOffset(0);
    setEntries([]);
    setFetchTrigger((prev) => prev + 1);
  }, []);

  const loadMore = useCallback(() => {
    if (!hasMore || loading) return;
    setOffset((prev) => prev + pageSize);
  }, [hasMore, loading, pageSize]);

  useEffect(() => {
    if (!apiClients || !organizationId || !channelId || !itemId) {
      setEntries([]);
      setTotal(0);
      setHasMore(false);
      return;
    }

    let cancelled = false;

    const fetchAudit = async () => {
      setLoading(true);
      setError(null);

      try {
        const response = await getItemAuditTrail(
          apiClients.schemaClient,
          channelId,
          itemId,
          { limit: pageSize, cursor: offset === 0 ? undefined : String(offset) }
        );

        if (cancelled) return;

        if (offset === 0) {
          setEntries(response.entries);
        } else {
          setEntries((prev) => [...prev, ...response.entries]);
        }
        setTotal(response.total);
        setHasMore(response.hasMore);
      } catch (err) {
        if (!cancelled) {
          const message =
            err instanceof Error ? err.message : 'Failed to load audit trail';
          setError(message);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    fetchAudit();

    return () => {
      cancelled = true;
    };
  }, [apiClients, organizationId, channelId, itemId, pageSize, offset, fetchTrigger]);

  useEffect(() => {
    setOffset(0);
    setEntries([]);
    setTotal(0);
    setHasMore(false);
  }, [channelId, itemId]);

  return { entries, total, hasMore, loading, error, loadMore, refetch };
}
